import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import pt from './locales/pt.json';
import it from './locales/it.json';
import nl from './locales/nl.json';
import hi from './locales/hi.json';
import ja from './locales/ja.json';
import zh from './locales/zh.json';
import ko from './locales/ko.json';
import ar from './locales/ar.json';

// Curated language set (label = English name, native = endonym shown in the picker).
export const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'it', label: 'Italian', native: 'Italiano' },
  { code: 'nl', label: 'Dutch', native: 'Nederlands' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
  { code: 'zh', label: 'Chinese', native: '简体中文' },
  { code: 'ko', label: 'Korean', native: '한국어' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
];
export const RTL_LANGS = ['ar'];
const SUPPORTED = LANGUAGES.map((l) => l.code);
const EXPLICIT_KEY = 'hg_lang_explicit';

const resources = { en, es, fr, de, pt, it, nl, hi, ja, zh, ko, ar };

function getLS(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

// The phone/browser's preferred language, if we support it.
function detectDeviceLanguage() {
  try {
    const langs = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const code = (l || '').slice(0, 2).toLowerCase();
      if (SUPPORTED.includes(code)) return code;
    }
  } catch { /* noop */ }
  return null;
}

const stored = getLS('hg_lang');
const explicitlyChosen = getLS(EXPLICIT_KEY) === '1';
// Boot order: an explicit past choice wins; otherwise open in the browser's language
// (auto-detect); fall back to any stored value, then English.
const bootLang = (explicitlyChosen && SUPPORTED.includes(stored))
  ? stored
  : (detectDeviceLanguage() || (SUPPORTED.includes(stored) ? stored : 'en'));

i18n.use(initReactI18next).init({
  resources: Object.fromEntries(Object.entries(resources).map(([k, v]) => [k, { translation: v }])),
  lng: bootLang,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

function setDocDir(lang) {
  if (typeof document !== 'undefined') {
    document.documentElement.dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }
}

export function applyLanguage(code, explicit = false) {
  const lang = SUPPORTED.includes(code) ? code : 'en';
  i18n.changeLanguage(lang);
  try {
    localStorage.setItem('hg_lang', lang);
    if (explicit) localStorage.setItem(EXPLICIT_KEY, '1');
  } catch { /* noop */ }
  setDocDir(lang);
}

export function hasExplicitLanguage() {
  return getLS(EXPLICIT_KEY) === '1';
}

// Apply the account's saved language on login — but don't override a language the user
// already picked on this device, and let auto-detect win over the default English.
export function applyAccountLanguage(code) {
  if (hasExplicitLanguage()) return;
  if (!code || code === 'en') return;
  applyLanguage(code, true);
}

// Set direction/lang for the boot language (without persisting a non-explicit detection).
setDocDir(i18n.language);

export default i18n;
